import { CSSProperties, FC, PropsWithChildren } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { css, cx } from '@linaria/atomic';
import { BgNoiseImg } from '@frontend/assets';
import { theme } from '@frontend/theme';
import { WeaveLogoIcon } from '@frontend/design-system';

interface GradientPageProps {
  /** @deprecated Emotion is being removed. Pass a linaria class via `className` instead of `pageStyles`. */
  pageStyles?: Interpolation<Theme>;
  className?: string;
  showWeaveIconAtBottom?: boolean;
  bgColor?: string;
  gradientColor1?: string;
  gradientColor2?: string;
}
export const GradientPage: FC<PropsWithChildren<GradientPageProps>> = ({
  children,
  pageStyles,
  className,
  showWeaveIconAtBottom,
  bgColor = theme.colors.white,
  gradientColor1 = theme.colors.secondary.seaweed10,
  gradientColor2 = theme.colors.indigo10,
}) => {
  const cssVars = {
    '--gp-noise-img': `url("${BgNoiseImg}")`,
    '--gp-bg-overlay': `${bgColor}cc`,
    '--gp-base-transparent': `${bgColor}00`,
    '--gp-gradient-1': gradientColor1,
    '--gp-gradient-2': gradientColor2,
  } as CSSProperties;

  return (
    <div className={cx(rootStyle, className)} style={cssVars} css={pageStyles}>
      <div className={gradientNoiseBGStyle}>
        <div className={gradientShape1Style} />
        <div className={gradientShape2Style} />
      </div>
      {showWeaveIconAtBottom && <WeaveLogoIcon size={32} className={weaveIconAtBottomStyle} />}
      {children}
    </div>
  );
};

const rootStyle = css`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
`;

const gradientNoiseBGStyle = css`
  width: 100%;
  height: 100%;
  position: absolute;
  background-image: var(--gp-noise-img);
  background-blend-mode: overlay;
  overflow: hidden;
  background-color: var(--gp-bg-overlay);
  background-repeat: no-repeat;
  background-size: cover;
  z-index: -1;
  container-type: size;
`;

const gradientShape1Style = css`
  position: absolute;
  width: auto;
  height: auto;
  aspect-ratio: 1/1;
  border-radius: ${theme.borderRadius.full};
  top: 0;
  right: 0;
  transform: rotate(0deg) translate(10%, -65%);
  background: radial-gradient(ellipse at 50% 50%, var(--gp-gradient-1) 50%, var(--gp-base-transparent) 70%);
  animation: moveAround1 16s ease infinite;
  mix-blend-mode: multiply;

  @container (orientation: portrait) {
    height: 100%;
  }

  @container (orientation: landscape) {
    width: 100%;
  }

  @keyframes moveAround1 {
    0% {
      transform: rotate(0deg) translate(10%, -65%);
      scale: 1;
    }
    25% {
      transform: rotate(10deg) translate(15%, -60%);
      scale: 1.1;
    }
    50% {
      transform: rotate(5deg) translate(5%, -70%);
      scale: 1.2;
    }
    75% {
      transform: rotate(-5deg) translate(20%, -55%);
      scale: 1.1;
    }
    100% {
      transform: rotate(0deg) translate(10%, -65%);
      scale: 1;
    }
  }
`;

const gradientShape2Style = css`
  position: absolute;
  width: auto;
  height: auto;
  aspect-ratio: 1.1/1;
  border-radius: ${theme.borderRadius.full};
  bottom: 0;
  right: 0;
  transform: rotate(-20deg) translate(30%, 60%);
  background: radial-gradient(ellipse at 50% 50%, var(--gp-gradient-2) 50%, var(--gp-base-transparent) 70%);
  animation: moveAround2 16s 3s ease infinite;
  mix-blend-mode: multiply;

  @container (orientation: portrait) {
    height: 100%;
  }

  @container (orientation: landscape) {
    width: 100%;
  }

  @keyframes moveAround2 {
    0% {
      transform: rotate(-20deg) translate(30%, 60%);
      scale: 1;
    }
    20% {
      transform: rotate(-10deg) translate(25%, 65%);
      scale: 1.1;
    }
    40% {
      transform: rotate(0deg) translate(20%, 70%);
      scale: 1.2;
    }
    60% {
      transform: rotate(10deg) translate(15%, 75%);
      scale: 1.1;
    }
    80% {
      transform: rotate(5deg) translate(20%, 70%);
      scale: 1.2;
    }
    100% {
      transform: rotate(-20deg) translate(30%, 60%);
      scale: 1;
    }
  }
`;

const weaveIconAtBottomStyle = css`
  position: absolute;
  bottom: ${theme.spacing(2)};
  left: 50%;
  transform: translateX(-50%);
  color: ${theme.colors.neutral30};
  z-index: -1;
`;
